import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';

interface KnowledgeItem {
  id: string;
  tenant_id: string;
  type: string;
  category: string;
  title: string;
  content: string;
  summary: string;
  source_type: string;
  source_file_name: string;
  embedding_status: string;
  embedding_error: string;
  chunk_count: number;
  last_indexed_at: string;
  is_published: number;
  version: number;
  created_at: string;
  updated_at: string;
  tags: string[];
  metadata: Record<string, unknown>;
  chunks: Array<{
    id: string;
    chunk_index: number;
    content_preview: string;
    section_header: string;
    token_count: number;
  }>;
}

export const Route = createFileRoute('/knowledge_/$id')({
  component: KnowledgeDetailPage,
});

function KnowledgeDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [item, setItem] = useState<KnowledgeItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Edit state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    loadItem();
  }, [id, currentTenant.id]);

  const loadItem = async () => {
    try {
      const response = await fetch(`/api/v1/knowledge/${id}`);
      if (response.ok) {
        const data = await response.json();
        setItem(data);
        setTitle(data.title);
        setCategory(data.category || '');
        setContent(data.content || '');
        setTags(data.tags?.join(', ') || '');
      } else if (currentTenant.id === 'arcteryx') {
        // Use demo data for Arc'teryx
        const demoItem = getArcteryxDemoKnowledgeItem(id);
        if (demoItem) {
          setItem(demoItem);
          setTitle(demoItem.title);
          setCategory(demoItem.category || '');
          setContent(demoItem.content || '');
          setTags(demoItem.tags?.join(', ') || '');
        }
      }
    } catch (error) {
      console.error('Failed to load:', error);
      // Fallback to demo data for Arc'teryx on error
      if (currentTenant.id === 'arcteryx') {
        const demoItem = getArcteryxDemoKnowledgeItem(id);
        if (demoItem) {
          setItem(demoItem);
          setTitle(demoItem.title);
          setCategory(demoItem.category || '');
          setContent(demoItem.content || '');
          setTags(demoItem.tags?.join(', ') || '');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/v1/knowledge/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          category: category || null,
          content,
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        }),
      });

      if (response.ok) {
        setEditing(false);
        loadItem();
      }
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`/api/v1/knowledge/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        navigate({ to: '/knowledge' });
      }
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleReindex = async () => {
    try {
      await fetch(`/api/v1/knowledge/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: item?.content }), // Triggers re-index
      });
      loadItem();
    } catch (error) {
      console.error('Failed to reindex:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'indexed': return '#00FBA9';
      case 'pending': return '#FFB800';
      case 'processing': return '#3941EB';
      case 'error': return '#FF4C00';
      default: return '#A9A9A9';
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: '#00FBA9' }} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <p style={{ color: '#A9A9A9' }}>Knowledge item not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate({ to: '/knowledge' })}
          className="text-sm flex items-center gap-1 mb-4"
          style={{ color: '#A9A9A9' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Knowledge Hub
        </button>

        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className="px-2 py-1 rounded text-xs uppercase"
                style={{ backgroundColor: '#FAFAFA', color: '#A9A9A9' }}
              >
                {item.type}
              </span>
              {item.category && (
                <span className="text-xs" style={{ color: '#A9A9A9' }}>
                  / {item.category}
                </span>
              )}
              <span
                className="flex items-center gap-1 text-xs"
                style={{ color: getStatusColor(item.embedding_status) }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor(item.embedding_status) }} />
                {item.embedding_status}
              </span>
            </div>
            {editing ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-2xl font-light w-full px-2 py-1 border rounded"
                style={{ color: '#171717', borderColor: 'rgba(169,169,169,0.3)' }}
              />
            ) : (
              <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
                {item.title}
              </h1>
            )}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ color: '#A9A9A9' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: '#00FBA9', color: '#171717' }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ backgroundColor: '#FAFAFA', color: '#171717' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ color: '#FF4C00' }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Content Panel */}
        <div className="col-span-2">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
                    Content
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full px-3 py-2 border text-sm font-mono min-h-[400px]"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>
                    Tags
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                    placeholder="Comma separated"
                  />
                </div>
              </div>
            ) : (
              <div 
                className="prose prose-sm max-w-none"
                style={{ color: '#171717' }}
                dangerouslySetInnerHTML={{ __html: simpleMarkdown(item.content || 'No content') }}
              />
            )}
          </div>

          {/* Tags */}
          {!editing && item.tags && item.tags.length > 0 && (
            <div className="mt-4 flex gap-2 flex-wrap">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: '#FAFAFA', color: '#A9A9A9' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Indexing Status */}
          <div className="bg-white border p-4" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: '#A9A9A9' }}>
              Indexing
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span style={{ color: '#A9A9A9' }}>Status</span>
                <span
                  className="flex items-center gap-1 capitalize"
                  style={{ color: getStatusColor(item.embedding_status) }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor(item.embedding_status) }} />
                  {item.embedding_status}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#A9A9A9' }}>Chunks</span>
                <span style={{ color: '#171717' }}>{item.chunk_count}</span>
              </div>
              {item.last_indexed_at && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Last indexed</span>
                  <span style={{ color: '#171717' }}>
                    {new Date(item.last_indexed_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              {item.embedding_error && (
                <div className="p-2 rounded text-xs" style={{ backgroundColor: 'rgba(255,76,0,0.1)', color: '#FF4C00' }}>
                  {item.embedding_error}
                </div>
              )}
              <button
                onClick={handleReindex}
                className="w-full px-3 py-2 text-xs"
                style={{ backgroundColor: '#FAFAFA', color: '#171717' }}
              >
                Re-index
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border p-4" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: '#A9A9A9' }}>
              Details
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span style={{ color: '#A9A9A9' }}>Source</span>
                <span style={{ color: '#171717' }}>{item.source_type}</span>
              </div>
              {item.source_file_name && (
                <div className="flex justify-between">
                  <span style={{ color: '#A9A9A9' }}>File</span>
                  <span style={{ color: '#171717' }}>{item.source_file_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span style={{ color: '#A9A9A9' }}>Version</span>
                <span style={{ color: '#171717' }}>v{item.version}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#A9A9A9' }}>Created</span>
                <span style={{ color: '#171717' }}>
                  {new Date(item.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#A9A9A9' }}>Updated</span>
                <span style={{ color: '#171717' }}>
                  {new Date(item.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Chunks Preview */}
          {item.chunks && item.chunks.length > 0 && (
            <div className="bg-white border p-4" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: '#A9A9A9' }}>
                Chunks ({item.chunks.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {item.chunks.map((chunk) => (
                  <div
                    key={chunk.id}
                    className="p-2 rounded text-xs"
                    style={{ backgroundColor: '#FAFAFA' }}
                  >
                    {chunk.section_header && (
                      <p className="font-medium mb-1" style={{ color: '#171717' }}>
                        {chunk.section_header}
                      </p>
                    )}
                    <p style={{ color: '#A9A9A9' }}>{chunk.content_preview}</p>
                    {chunk.token_count && (
                      <p className="mt-1" style={{ color: '#A9A9A9' }}>
                        ~{chunk.token_count} tokens
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-md">
            <h2 className="text-lg font-medium mb-2" style={{ color: '#171717' }}>Delete Knowledge</h2>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              Are you sure you want to delete "{item.title}"? This will also remove it from the AI Educator's knowledge base.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-full text-sm"
                style={{ color: '#A9A9A9' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: '#FF4C00', color: '#FFFFFF' }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function simpleMarkdown(text: string): string {
  return text
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-medium mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-medium mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-medium mt-6 mb-3">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
    .replace(/\n\n/g, '</p><p class="mb-3">')
    .replace(/\n/g, '<br/>');
}

// Demo knowledge data for Arc'teryx tenant
function getArcteryxDemoKnowledgeItem(id: string): KnowledgeItem | null {
  const demoItems: Record<string, KnowledgeItem> = {
    'k-mat-1': {
      id: 'k-mat-1',
      tenant_id: 'arcteryx',
      type: 'material',
      category: 'Shell Technologies',
      title: 'GORE-TEX Pro Technology',
      content: `# GORE-TEX Pro Technology

## Overview
GORE-TEX Pro is the most breathable, most rugged, and durably waterproof GORE-TEX product technology available. It's designed for extended use in demanding conditions.

## Key Features
- **Durably Waterproof**: Engineered to provide lasting waterproof protection
- **Highly Breathable**: Superior moisture vapor transfer keeps you comfortable during high-output activities
- **Windproof**: Complete protection from wind chill
- **Lightweight**: Optimized weight-to-performance ratio

## Construction
The three-layer construction bonds the GORE-TEX membrane between a tough outer face fabric and an inner lining. This creates a single, unified material that:
- Resists wear and abrasion
- Maintains breathability over time
- Provides consistent waterproof protection

## Care Instructions
- Machine wash warm with liquid detergent
- Avoid fabric softeners and bleach
- Tumble dry on medium heat to reactivate DWR
- Do not dry clean

## Used In
- Alpha SV Jacket
- Beta AR Jacket
- Alpha Parka
- Sabre AR Jacket`,
      summary: 'The most breathable, most rugged, and durably waterproof GORE-TEX product technology. Used in our most demanding alpine shells.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 8,
      last_indexed_at: '2024-12-15T10:00:00Z',
      is_published: 1,
      version: 1,
      created_at: '2024-12-01T10:00:00Z',
      updated_at: '2024-12-15T10:00:00Z',
      tags: ['gore-tex', 'waterproof', 'breathable', 'alpine'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'GORE-TEX Pro is the most breathable, most rugged...', section_header: 'Overview', token_count: 156 },
        { id: 'c2', chunk_index: 1, content_preview: 'Durably Waterproof: Engineered to provide lasting...', section_header: 'Key Features', token_count: 203 },
        { id: 'c3', chunk_index: 2, content_preview: 'The three-layer construction bonds the GORE-TEX membrane...', section_header: 'Construction', token_count: 178 },
      ],
    },
    'k-mat-2': {
      id: 'k-mat-2',
      tenant_id: 'arcteryx',
      type: 'material',
      category: 'Shell Technologies',
      title: 'GORE-TEX with C-KNIT Backer',
      content: `# GORE-TEX with C-KNIT Backer

## Overview
GORE-TEX with C-KNIT backer technology offers exceptional comfort and breathability in a lighter weight package. The circular knit backer provides next-to-skin comfort ideal for hiking and trekking.

## Key Benefits
- **Superior Comfort**: Soft, circular knit backer feels comfortable against skin
- **Lighter Weight**: Up to 10% lighter than conventional GORE-TEX products
- **Highly Breathable**: Enhanced moisture vapor transmission
- **Waterproof & Windproof**: Complete weather protection

## Ideal For
- Long-distance hiking
- Backpacking
- Trekking
- Light alpine activities

## Products Featuring C-KNIT
- Beta SL Jacket
- Zeta SL Jacket
- Beta LT Jacket`,
      summary: 'Lighter, more comfortable shell technology with a circular knit backer for next-to-skin comfort. Ideal for hiking and trekking.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 6,
      last_indexed_at: '2024-12-14T10:00:00Z',
      is_published: 1,
      version: 1,
      created_at: '2024-12-01T10:00:00Z',
      updated_at: '2024-12-14T10:00:00Z',
      tags: ['gore-tex', 'c-knit', 'comfort', 'hiking'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'GORE-TEX with C-KNIT backer technology offers exceptional comfort...', section_header: 'Overview', token_count: 145 },
        { id: 'c2', chunk_index: 1, content_preview: 'Superior Comfort: Soft, circular knit backer feels comfortable...', section_header: 'Key Benefits', token_count: 189 },
      ],
    },
    'k-mat-3': {
      id: 'k-mat-3',
      tenant_id: 'arcteryx',
      type: 'material',
      category: 'Insulation',
      title: 'Coreloft™ Synthetic Insulation',
      content: `# Coreloft™ Synthetic Insulation

## Overview
Coreloft is Arc'teryx's proprietary synthetic insulation. It provides excellent warmth-to-weight ratio and crucially maintains insulating properties even when wet.

## Key Characteristics
- **Warm When Wet**: Unlike down, Coreloft retains warmth even in damp conditions
- **Quick Drying**: Synthetic fibers dry faster than natural down
- **Durable**: Maintains loft and performance over time
- **Hypoallergenic**: Suitable for those with down allergies

## Coreloft Variants
- **Coreloft**: Standard synthetic insulation
- **Coreloft Compact**: Higher density for packability
- **Coreloft Continuous**: Continuous filament for durability

## Care Instructions
- Machine wash on gentle cycle
- Tumble dry on low heat
- Do not iron
- Do not dry clean`,
      summary: 'Our proprietary synthetic insulation that provides excellent warmth-to-weight ratio and retains insulating properties when wet.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 5,
      last_indexed_at: '2024-12-13T10:00:00Z',
      is_published: 1,
      version: 1,
      created_at: '2024-12-01T10:00:00Z',
      updated_at: '2024-12-13T10:00:00Z',
      tags: ['coreloft', 'insulation', 'synthetic', 'warmth'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'Coreloft is Arc\'teryx\'s proprietary synthetic insulation...', section_header: 'Overview', token_count: 134 },
        { id: 'c2', chunk_index: 1, content_preview: 'Warm When Wet: Unlike down, Coreloft retains warmth...', section_header: 'Key Characteristics', token_count: 167 },
      ],
    },
    'k-pol-1': {
      id: 'k-pol-1',
      tenant_id: 'arcteryx',
      type: 'policy',
      category: 'Trade-In Program',
      title: 'Trade-In Program Terms & Conditions',
      content: `# Arc'teryx ReBird Trade-In Program

## Overview
The Arc'teryx ReBird trade-in program allows customers to return used Arc'teryx gear in exchange for store credit. This supports our commitment to extending product life and reducing waste.

## Eligibility
- All Arc'teryx branded products are eligible
- Products must be reasonably clean and free of major damage
- Items with non-Arc'teryx modifications are not eligible
- Counterfeit products are not accepted

## Credit Values
Credit values are determined based on:
- **Original retail price** of the product
- **Current condition** assessment
- **Age** and **style** of the product
- **Market demand** for the item

### Condition Grades
- **Like New (90%+ credit)**: Tags attached or minimal wear
- **Excellent (70-89% credit)**: Light use, no visible defects
- **Good (50-69% credit)**: Normal wear, minor repairs possible
- **Fair (30-49% credit)**: Significant wear, repairs needed

## Store Credit Terms
- Credits issued as digital store credit
- Valid for 12 months from issue date
- Usable online or in Arc'teryx stores
- Non-transferable
- Cannot be exchanged for cash`,
      summary: 'Complete terms for Arc\'teryx ReBird trade-in program. Includes eligibility requirements, credit values, and product conditions.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 12,
      last_indexed_at: '2024-12-18T10:00:00Z',
      is_published: 1,
      version: 2,
      created_at: '2024-11-01T10:00:00Z',
      updated_at: '2024-12-18T10:00:00Z',
      tags: ['trade-in', 'rebird', 'terms', 'conditions'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'The Arc\'teryx ReBird trade-in program allows customers...', section_header: 'Overview', token_count: 189 },
        { id: 'c2', chunk_index: 1, content_preview: 'All Arc\'teryx branded products are eligible...', section_header: 'Eligibility', token_count: 156 },
        { id: 'c3', chunk_index: 2, content_preview: 'Like New (90%+ credit): Tags attached or minimal wear...', section_header: 'Condition Grades', token_count: 234 },
      ],
    },
    'k-faq-1': {
      id: 'k-faq-1',
      tenant_id: 'arcteryx',
      type: 'faq',
      category: 'Trade-In Program',
      title: 'How does trade-in credit work?',
      content: `# How Trade-In Credit Works

## Getting Your Credit
1. Submit your item for trade-in assessment
2. Receive condition grading and credit offer
3. Accept the offer to receive store credit
4. Credit is added to your account within 24 hours

## Using Your Credit
- **Online**: Apply at checkout
- **In-Store**: Show your account at register
- **Combine**: Use with other payment methods

## Important Notes
- Credits expire 12 months from issue
- No minimum purchase required
- Can be used on sale items
- One credit per transaction`,
      summary: 'Trade-in credits are issued as store credit valid for 12 months. Credits can be used online or in-store for any Arc\'teryx products.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 3,
      last_indexed_at: '2024-12-18T10:00:00Z',
      is_published: 1,
      version: 1,
      created_at: '2024-12-01T10:00:00Z',
      updated_at: '2024-12-18T10:00:00Z',
      tags: ['trade-in', 'credit', 'store-credit', 'faq'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'Submit your item for trade-in assessment...', section_header: 'Getting Your Credit', token_count: 98 },
        { id: 'c2', chunk_index: 1, content_preview: 'Online: Apply at checkout. In-Store: Show your account...', section_header: 'Using Your Credit', token_count: 87 },
      ],
    },
    'k-faq-2': {
      id: 'k-faq-2',
      tenant_id: 'arcteryx',
      type: 'faq',
      category: 'Product Care',
      title: 'How to wash GORE-TEX jackets?',
      content: `# Washing GORE-TEX Jackets

## Before Washing
- Close all zippers and velcro
- Empty all pockets
- Shake out loose debris

## Washing Instructions
1. **Machine wash warm** (40°C/104°F)
2. Use **liquid detergent** (no powder)
3. Run an **extra rinse cycle**
4. **No fabric softener** - damages DWR coating

## Drying
- **Tumble dry on medium heat** for 20-30 minutes
- Heat reactivates the DWR (Durable Water Repellent)
- Alternatively, use a warm iron on low setting (no steam)

## What to Avoid
- ❌ Fabric softeners
- ❌ Bleach
- ❌ Dry cleaning
- ❌ High heat settings

## Restoring DWR
If water stops beading on the surface:
1. Wash the garment
2. Tumble dry on medium heat
3. If still not beading, apply spray-on DWR treatment`,
      summary: 'Machine wash warm with tech wash, no fabric softener. Tumble dry on medium heat to reactivate DWR. Avoid dry cleaning.',
      source_type: 'manual',
      source_file_name: '',
      embedding_status: 'indexed',
      embedding_error: '',
      chunk_count: 4,
      last_indexed_at: '2024-12-17T10:00:00Z',
      is_published: 1,
      version: 1,
      created_at: '2024-12-01T10:00:00Z',
      updated_at: '2024-12-17T10:00:00Z',
      tags: ['care', 'washing', 'gore-tex', 'maintenance'],
      metadata: {},
      chunks: [
        { id: 'c1', chunk_index: 0, content_preview: 'Close all zippers and velcro. Empty all pockets...', section_header: 'Before Washing', token_count: 67 },
        { id: 'c2', chunk_index: 1, content_preview: 'Machine wash warm (40°C/104°F). Use liquid detergent...', section_header: 'Washing Instructions', token_count: 112 },
        { id: 'c3', chunk_index: 2, content_preview: 'Tumble dry on medium heat for 20-30 minutes...', section_header: 'Drying', token_count: 89 },
      ],
    },
  };

  return demoItems[id] || null;
}


